import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, ArrowLeft } from 'lucide-react';
import { Button, Card, EmptyState, FilterBar, GroupByDropdown, ViewSwitcher, CalendarNavigation, ListDateCarousel, PrintButton } from '@/components/ui';
import { WeeklyView, MonthlyView } from '@/components/calendar';
import { useItemStore, useUIStore, useSettingsStore } from '@/stores';
import { TaskList } from './TaskList';
import { isRecurringItem, extendItemOccurrences } from '@perfin/shared';
import type { TabKey } from '@/stores/uiStore';

export function TasksPage() {
  const navigate = useNavigate();
  const tasks = useItemStore((state) => state.getTasks());
  const { activeTab, setActiveTab, categoryFilters, pageFilters, viewMode, calendarOffset, groupBy } = useUIStore();
  const { currentUserId, getCategoriesForType, members } = useSettingsStore();
  const isAdmin = useSettingsStore((state) => state.currentUserRole === 'admin' || state.currentUserRole === 'superAdmin');
  const currentViewMode = viewMode?.tasks ?? 'card';
  const currentTab = activeTab?.tasks ?? 'today';
  const currentGroupBy = groupBy?.tasks ?? 'date';
  const pFilters = pageFilters?.tasks ?? { ownerFilter: null, statusFilter: null, priorityFilter: null };

  // Calendar views show all scheduled AND recurring items
  const isCalendarView = currentViewMode === 'weekly' || currentViewMode === 'monthly';
  const effectiveTab = isCalendarView ? 'allScheduledAndRecurring' : currentTab;

  // Bug #2: Auto-switch groupBy when 'date' is invalid for All Recurring tab
  const { setGroupBy } = useUIStore();
  React.useEffect(() => {
    if (currentTab === 'allRecurring' && currentGroupBy === 'date') {
      setGroupBy('tasks', 'category');
    }
  }, [currentTab, currentGroupBy, setGroupBy]);

  // Bug #5: Validate owner filter - if references non-existent user, reset to current user
  const { setOwnerFilter } = useUIStore();
  React.useEffect(() => {
    if (pFilters.ownerFilter && members.length > 0) {
      const ownerExists = members.some(m => m.userId === pFilters.ownerFilter);
      if (!ownerExists && currentUserId) {
        setOwnerFilter('tasks', currentUserId);
      }
    }
  }, [pFilters.ownerFilter, members, currentUserId, setOwnerFilter]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'allScheduled', label: 'All Scheduled' },
    { key: 'allUnscheduled', label: 'All Unscheduled' },
    { key: 'allRecurring', label: 'All Recurring' },
    { key: 'pastWeek', label: 'Past Week' },
    { key: 'today', label: 'Today' },
    { key: 'nextWeek', label: 'Next Week' },
  ];

  // Get reference date from store (for Today tab carousel - affects all time-based tabs)
  const getReferenceDate = useUIStore((state) => state.getReferenceDate);
  const referenceDate = getReferenceDate('tasks');

  const filteredTasks = useMemo(() => {
    // Use referenceDate for time-based tabs (Today carousel can change this)
    const refDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
    const refDatePlusOne = new Date(refDate.getTime() + 24 * 60 * 60 * 1000);
    const sevenDaysBefore = new Date(refDate.getTime() - 7 * 24 * 60 * 60 * 1000);
    const sevenDaysAfter = new Date(refDate.getTime() + 7 * 24 * 60 * 60 * 1000);

    const parseDate = (d: unknown): Date | null => {
      if (!d) return null;
      if (typeof (d as { toDate?: () => Date }).toDate === 'function') {
        return (d as { toDate: () => Date }).toDate();
      }
      if (d instanceof Date) return d;
      if (typeof d === 'object' && d !== null && 'seconds' in d) {
        return new Date((d as { seconds: number }).seconds * 1000);
      }
      return new Date(d as string | number);
    };

    let result = tasks;

    // 0. Exclude choreWheel tasks (they appear on ChoreWheelPage, not TasksPage)
    result = result.filter((task) => !task.choreWheel);

    // 1. Apply filters FIRST (category, status, priority)
    const filter = categoryFilters.tasks;
    if (filter.categoryId) {
      result = result.filter((task) => task.category === filter.categoryId);
    }
    if (filter.subCategoryId) {
      result = result.filter((task) => task.subCategory === filter.subCategoryId);
    }
    if (pFilters?.itemGroupFilter) {
      result = result.filter((task) => task.itemGroup === pFilters.itemGroupFilter);
    }

    // Status filter: for time-based tabs, only apply to non-recurring items
    // (recurring items will be filtered by occurrence status in TaskList)
    const isTimeBased = effectiveTab === 'pastWeek' || effectiveTab === 'today' || effectiveTab === 'nextWeek';
    if (pFilters?.statusFilter) {
      result = result.filter((task) => {
        if (isTimeBased && isRecurringItem(task)) {
          return true; // Let TaskList filter by occurrence status
        }
        return task.status === pFilters.statusFilter;
      });
    }

    if (pFilters?.priorityFilter) {
      result = result.filter((task) => task.priority === pFilters.priorityFilter);
    }

    // Owner filtering
    if (pFilters?.ownerFilter) {
      result = result.filter((task) => task.owner === pFilters.ownerFilter);
    } else if (!isAdmin && currentUserId) {
      // Standard users without owner filter: show items they own OR created
      result = result.filter((task) => task.owner === currentUserId || task.createdBy === currentUserId);
    }

    // 2. Apply tab (time) filter on the result (use effectiveTab for calendar view compatibility)
    switch (effectiveTab) {
      case 'allScheduledAndRecurring':
        // For calendar views: include scheduled non-recurring items AND all recurring items
        result = result.filter((task) => {
          if (isRecurringItem(task)) return true; // Include recurring
          const dueDate = parseDate(task.dueDate);
          return !!dueDate; // Include scheduled non-recurring
        });
        break;

      case 'allScheduled':
        // NON-RECURRING items with dueDate (scheduled = has a specific due date)
        result = result.filter((task) => {
          if (isRecurringItem(task)) return false; // Exclude recurring - they go to All Recurring tab
          const dueDate = parseDate(task.dueDate);
          return !!dueDate;
        });
        break;

      case 'allUnscheduled':
        // NON-RECURRING items with no due date
        result = result.filter((task) => {
          if (isRecurringItem(task)) return false;
          const dueDate = parseDate(task.dueDate);
          return !dueDate;
        });
        break;

      case 'allRecurring':
        // All recurring items (regardless of dates) - NO tile replication here
        result = result.filter((task) => isRecurringItem(task));
        break;

      case 'pastWeek':
        // Scheduled items due in 7 days before refDate, OR recurring with occurrences in that range
        // Excludes unscheduled items
        result = result.filter((task) => {
          if (isRecurringItem(task)) {
            const occurrences = (task as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= sevenDaysBefore && occDate < refDate;
            });
          }
          // Non-recurring: must have dueDate (exclude unscheduled)
          const dueDate = parseDate(task.dueDate);
          return dueDate && dueDate >= sevenDaysBefore && dueDate < refDate;
        });
        break;

      case 'today':
        // Scheduled items due on refDate, OR recurring with occurrence on refDate
        // Excludes unscheduled items
        result = result.filter((task) => {
          if (isRecurringItem(task)) {
            const occurrences = (task as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDate && occDate < refDatePlusOne;
            });
          }
          // Non-recurring: must have dueDate (exclude unscheduled)
          const dueDate = parseDate(task.dueDate);
          return dueDate && dueDate >= refDate && dueDate < refDatePlusOne;
        });
        break;

      case 'nextWeek':
        // Scheduled items due in 7 days after refDate, OR recurring with occurrences in that range
        // Excludes unscheduled items
        result = result.filter((task) => {
          if (isRecurringItem(task)) {
            const occurrences = (task as unknown as { occurrences?: Array<{ date: unknown }> }).occurrences || [];
            return occurrences.some(occ => {
              const occDate = parseDate(occ.date);
              return occDate && occDate >= refDatePlusOne && occDate <= sevenDaysAfter;
            });
          }
          // Non-recurring: must have dueDate (exclude unscheduled)
          const dueDate = parseDate(task.dueDate);
          return dueDate && dueDate >= refDatePlusOne && dueDate <= sevenDaysAfter;
        });
        break;
    }

    return result;
  }, [tasks, effectiveTab, categoryFilters.tasks, pFilters, currentUserId, isAdmin, referenceDate]);

  const handleAddTask = () => navigate('/items/new?mode=create&type=task');

  // Handler for extending occurrences when navigating beyond 180-day horizon
  const handleNavigateBeyondHorizon = useCallback(async (targetDate: Date) => {
    // Get all recurring task IDs
    const recurringTaskIds = tasks
      .filter((task) => isRecurringItem(task))
      .map((task) => task.id);

    if (recurringTaskIds.length > 0) {
      try {
        await extendItemOccurrences(recurringTaskIds, targetDate);
      } catch (error) {
        console.error('Failed to extend occurrences:', error);
      }
    }
  }, [tasks]);

  // Helper functions for PDF generation
  const taskCategories = getCategoriesForType('task');
  const getCategoryName = (categoryId: string) => {
    const cat = taskCategories.find((c) => c.id === categoryId);
    return cat?.name || categoryId;
  };
  const getOwnerName = (ownerId: string) => {
    const member = members.find((m) => m.userId === ownerId);
    return member?.displayName || ownerId;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <PrintButton
            title="Tasks"
            items={filteredTasks}
            getCategoryName={getCategoryName}
            getOwnerName={getOwnerName}
          />
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <FilterBar itemType="task" pageKey="tasks" hideStatusFilter={currentTab === 'allRecurring'} />
        <hr className="w-full border-gray-300 md:hidden" />
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          {currentViewMode === 'card' && <GroupByDropdown pageKey="tasks" showInfoTooltip excludeOptions={currentTab === 'allRecurring' ? ['date'] : []} />}
          <Button onClick={handleAddTask}>
            Add Task
          </Button>
          <ViewSwitcher pageKey="tasks" />
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex gap-1 flex-wrap md:flex-nowrap">
          {/* In calendar view, show single "All Scheduled and Recurring" tab */}
          {isCalendarView ? (
            <button className="px-4 py-2 text-sm font-medium border-b-2 border-primary-600 text-primary-600">
              All Scheduled and Recurring
            </button>
          ) : (
            tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab('tasks', tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  currentTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))
          )}
        </div>
        {currentViewMode === 'card' && (currentTab === 'pastWeek' || currentTab === 'today' || currentTab === 'nextWeek') && (
          <ListDateCarousel pageKey="tasks" />
        )}
        {currentViewMode !== 'card' && (
          <CalendarNavigation
            pageKey="tasks"
            mode={currentViewMode}
            onNavigateBeyondHorizon={handleNavigateBeyondHorizon}
          />
        )}
      </div>

      {/* Card View */}
      {currentViewMode === 'card' && (
        filteredTasks.length === 0 ? (
          <Card>
            <EmptyState
              icon={<CheckSquare className="h-12 w-12" />}
              title="No tasks found"
              description={
                currentTab === 'allScheduled'
                  ? 'Create your first task to get started'
                  : `No tasks in this view`
              }
              action={
                currentTab === 'allScheduled'
                  ? { label: 'Add Task', onClick: handleAddTask }
                  : undefined
              }
            />
          </Card>
        ) : (
          <TaskList tasks={filteredTasks} groupBy={currentGroupBy} currentTab={currentTab} hideStatusToggle={currentTab === 'allRecurring'} referenceDate={referenceDate} statusFilter={pFilters.statusFilter} />
        )
      )}

      {/* Weekly View */}
      {currentViewMode === 'weekly' && (
        <WeeklyView
          items={filteredTasks}
          offset={calendarOffset.tasks.weekly}
          basePath="/tasks"
        />
      )}

      {/* Monthly View */}
      {currentViewMode === 'monthly' && (
        <MonthlyView
          items={filteredTasks}
          offset={calendarOffset.tasks.monthly}
          basePath="/tasks"
        />
      )}
    </div>
  );
}
